import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserRound, MapPin, Building2, Save, ShieldCheck, Download, Trash2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, Spinner, Pill } from "../components/common";
import { useAuth } from "../context/AuthContext";
import PushToggle from "../components/PushToggle";

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const YEARS = (() => { const y = new Date().getFullYear() - 13; return Array.from({ length: 95 }, (_, i) => y - i); })();
const ACCOUNT_TYPES = [
  ["individual", "Individual"], ["business", "Business"], ["both", "Both"],
];

export default function ProfileSettings() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const u = r.data;
      setForm({
        first_name: u.first_name || "", last_name: u.last_name || "",
        gender: u.gender || "", birth_year: u.birth_year || "",
        location: { country: u.location?.country || "", region: u.location?.region || "", city: u.location?.city || "" },
        account_type: u.account_type || "individual",
        email: u.email, phone: u.phone, picture: u.picture, provider: u.auth_provider, name: u.name,
      });
    }).catch(() => setForm(false));
  }, []);

  if (!user) return (
    <div className="max-w-3xl mx-auto px-5 py-20 text-center">
      <UserRound className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
      <p className="text-muted-foreground">Please <Link to="/login" className="text-primary underline">sign in</Link> to edit your profile.</p>
    </div>
  );
  if (form === null) return <Spinner full />;
  if (form === false) return <p className="text-center py-20 text-muted-foreground">Could not load your profile — try again shortly.</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLoc = (k, v) => setForm((f) => ({ ...f, location: { ...f.location, [k]: v } }));
  const inp = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary";
  const lbl = "block text-xs font-600 text-zinc-600 mb-1";

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/auth/profile", {
        first_name: form.first_name, last_name: form.last_name, gender: form.gender || undefined,
        birth_year: form.birth_year || undefined, location: form.location, account_type: form.account_type,
      });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save your profile"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-settings-page">
      <PageHeader plain eyebrow="Account" title="Profile Settings"
        subtitle="Your name, details and location — used across TravelerPulse and shown with your community posts." />
      <div className="max-w-2xl mx-auto px-5 lg:px-8 pb-16">
        <div className="border border-zinc-300 bg-white p-6">
          {/* Identity (read-only) */}
          <div className="flex items-center gap-3 pb-4 border-b border-zinc-200">
            {form.picture
              ? <img src={form.picture} alt="" className="w-12 h-12 rounded-full border border-zinc-200" />
              : <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center"><UserRound className="w-6 h-6 text-zinc-400" /></div>}
            <div>
              <div className="font-heading font-900">{form.name || "Traveller"}</div>
              <div className="text-xs text-muted-foreground">{form.email || form.phone} · signed in via {form.provider || "Firebase"}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div><label className={lbl}>First name</label>
              <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="profile-first-name" className={inp} /></div>
            <div><label className={lbl}>Last name</label>
              <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="profile-last-name" className={inp} /></div>
            <div><label className={lbl}>Gender</label>
              <select value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="profile-gender" className={inp}>
                <option value="">Select…</option>{GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select></div>
            <div><label className={lbl}>Year of birth</label>
              <select value={form.birth_year} onChange={(e) => set("birth_year", e.target.value)} data-testid="profile-birth-year" className={inp}>
                <option value="">Year</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select></div>
          </div>

          <div className="mt-5 border border-zinc-200 bg-zinc-50/60 p-3" data-testid="profile-location">
            <div className="text-xs font-600 text-zinc-600 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> Location</div>
            <div className="flex flex-wrap gap-2">
              <input value={form.location.country} onChange={(e) => setLoc("country", e.target.value)} data-testid="profile-loc-country" placeholder="Country" className="flex-1 min-w-[110px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
              <input value={form.location.region} onChange={(e) => setLoc("region", e.target.value)} data-testid="profile-loc-region" placeholder="State / Region" className="flex-1 min-w-[100px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
              <input value={form.location.city} onChange={(e) => setLoc("city", e.target.value)} data-testid="profile-loc-city" placeholder="City" className="flex-1 min-w-[100px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="mt-5">
            <label className={lbl}>Account type</label>
            <div className="flex border border-zinc-300 max-w-sm">
              {ACCOUNT_TYPES.map(([k, label]) => (
                <button key={k} onClick={() => set("account_type", k)} data-testid={`profile-account-${k}`}
                  className={`flex-1 py-2 text-sm font-600 transition-colors ${form.account_type === k ? "bg-primary text-white" : "bg-white hover:bg-zinc-50"}`}>
                  {label}
                </button>
              ))}
            </div>
            {form.account_type !== "individual" && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-primary" />
                Business accounts manage their listings from <button onClick={() => nav("/my-business")} className="text-primary underline" data-testid="profile-goto-business">My Airport Business</button>.
              </p>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="profile-save-btn"
              className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 text-sm font-700 hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save profile"}
            </button>
            {user.role !== "user" && <Pill tone="blue">{user.role}</Pill>}
          </div>
        </div>

        {/* Notifications — one-tap push registration */}
        <div className="mt-6 border border-zinc-300 bg-white p-6" data-testid="profile-notifications-block">
          <h2 className="font-serif font-800 text-lg text-primary mb-1">Notifications</h2>
          <p className="text-xs text-muted-foreground mb-4">Manage how we reach you on this device.</p>
          <PushToggle />
        </div>

        {/* Privacy, data & ownership — spec §16 + §17 surfaced in Settings */}
        <div className="mt-6 border border-zinc-300 bg-white p-6" data-testid="profile-privacy-block">
          <h2 className="font-serif font-800 text-lg text-primary mb-1 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Privacy, data & ownership
          </h2>
          <p className="text-xs text-muted-foreground mb-4">Your data is yours — export it, delete individual modules or transfer your entire hub to another adult.</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <Link to="/settings/privacy" data-testid="settings-privacy-link"
              className="border border-slate-300 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-50 transition-colors">
              <div className="flex items-center gap-2 text-sm font-800 text-slate-800"><Download className="w-4 h-4 text-blue-700" /> Data export</div>
              <div className="text-xs text-slate-500 mt-0.5">Download every piece of your Travel Hub as JSON.</div>
            </Link>
            <Link to="/settings/privacy" data-testid="settings-delete-link"
              className="border border-slate-300 rounded-lg p-3 hover:border-rose-500 hover:bg-rose-50 transition-colors">
              <div className="flex items-center gap-2 text-sm font-800 text-slate-800"><Trash2 className="w-4 h-4 text-rose-700" /> Delete a module</div>
              <div className="text-xs text-slate-500 mt-0.5">Wipe trips, bags, loyalty, journeys or your entire hub.</div>
            </Link>
            <Link to="/settings/ownership-transfer" data-testid="settings-ownership-link"
              className="border border-slate-300 rounded-lg p-3 hover:border-fuchsia-500 hover:bg-fuchsia-50 transition-colors">
              <div className="flex items-center gap-2 text-sm font-800 text-slate-800"><ArrowRightLeft className="w-4 h-4 text-fuchsia-700" /> Transfer ownership</div>
              <div className="text-xs text-slate-500 mt-0.5">Move your entire hub to another adult's account (§17).</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
